import { ColoredChip } from "@components/colored-elements";
import { ContentDetail } from "@lib/content";
import { Tooltip } from "@material-ui/core";
import { blue, deepOrange, teal } from "@material-ui/core/colors";
import { COUNTRY_INFO } from 'pages/constants';
import { uniq } from "lodash";
import { MatchReason } from "./types";

export const CellForContentGenres = ({ value }) => {
  const MAX_VISIBLE_GENRES = 1;

  const listOfContentGenres = value
    ? uniq(
        value
          .split(',')
          .map((genre) => genre.trim())
          .filter(Boolean)
      )
    : [];

  if (!listOfContentGenres.length) {
    return null;
  }

  const visibleGenres = listOfContentGenres.slice(0, MAX_VISIBLE_GENRES);
  const remainingGenres = listOfContentGenres.slice(MAX_VISIBLE_GENRES);
  const remainingCount = remainingGenres.length;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '3px',
      }}
    >
      {visibleGenres.map((genre, index) => (
        <div
          key={`${genre}-${index}`}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title={genre}>
            <ColoredChip
              colored={teal[500]}
              size="small"
              label={genre}
              style={{fontSize:10}}
            />
          </Tooltip>

          {index === visibleGenres.length - 1 && remainingCount > 0 && (
            <Tooltip title={remainingGenres.join(', ')}>
              <span
                style={{
                  fontWeight: 'bold',
                  paddingLeft: '3px',
                  cursor: 'pointer',
                }}
              >
                +{remainingCount}
              </span>
            </Tooltip>
          )}
        </div>
      ))}
    </div>
  );
};

export const CellForContentLanguages = ({
  value
}: {
  value: string;
}) => {
  const MAX_VISIBLE = 1;

  const languages = value
    ? uniq(
        value
          .split(',')
          .map((language) => language.trim())
          .filter(Boolean)
      )
    : [];

  if (!languages.length) {
    return null;
  }

  const visibleLanguages = languages.slice(0, MAX_VISIBLE);
  const remainingLanguages = languages.slice(MAX_VISIBLE);
  const remainingCount = remainingLanguages.length;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '3px',
      }}
    >
      {visibleLanguages.map((language, index) => (
        <div
          key={`${language}-${index}`}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title={language}>
            <ColoredChip
              colored={deepOrange[100]}
              size="small"
              label={language}
                            style={{fontSize:10}}
            />
          </Tooltip>

          {index === visibleLanguages.length - 1 && remainingCount > 0 && (
            <Tooltip title={remainingLanguages.join(', ')}>
              <span
                style={{
                  fontWeight: 'bold',
                  paddingLeft: '3px',
                  cursor: 'pointer',
                }}
              >
                +{remainingCount}
              </span>
            </Tooltip>
          )}
        </div>
      ))}
    </div>
  );
};

export const originCountryNames = (value?: string) => {
  const countryIds = value ? value.split(',').map(code => code.trim()) : [];

  return COUNTRY_INFO.filter(({ code = '' }) => countryIds.includes(code))
    .map(({ name }) => name)
    .join(', ');
};

const CONTENT_SOURCE_LABELS: Record<number, string> = {
  4001: 'DELI',
  1001: 'TMDB',
  5001: 'Seller Catalogue',
  2001: 'OMDB',
  3001: 'GVD',
};

export const CellForContentSource = ({
  value
}: {
  value: string;
}) => {
  const datasourceId = value

  if (!datasourceId) {
    return null;
  }

  const sourceLabel =
    CONTENT_SOURCE_LABELS[datasourceId] ?? 'NA';

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '3px',
      }}
    >
      <Tooltip title={sourceLabel}>
        <ColoredChip
          colored={blue[800]}
          size="small"
          label={sourceLabel}
              style={{fontSize:10}}
        />
      </Tooltip>
    </div>
  );
};

/* ============================================================
   FORMAT DATE + TIME
   ============================================================ */

export const formatDateTime = (
  value: Date
): string => {

  return value.toLocaleString(
    'en-GB',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  );
};



/* ============================================================
   FORMAT GROUP NAME
   ============================================================ */

export const formatGroupName = (
  groupId: string
): string => {

  if (
    groupId.startsWith('DG-')
  ) {

    return `Group ${groupId
      .replace(
        'DG-',
        ''
      )
      .padStart(
        3,
        '0'
      )}`;
  }


  return groupId;
};


/* ============================================================
   FORMAT DATE
   ============================================================ */

export const formatDate = (
  value?: string
): string => {

  if (!value) {
    return '—';
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }


  return date.toLocaleDateString(
    'en-GB',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }
  );
};



/* ============================================================
   MATCH REASON LABEL
   ============================================================ */

export const formatMatchReason = (
  reason: MatchReason
): string => {

  const labels: Record<
    MatchReason,
    string
  > = {

    TITLE_MATCH:
      'Title Match',

    ORIGINAL_TITLE_MATCH:
      'Original Title',

    AKA_MATCH:
      'AKA Match',

    WORKING_TITLE_MATCH:
      'Working Title',

    IMDB_MATCH:
      'IMDb Match',

    TITLE_CONTENT_TYPE_MATCH:
      'Title + Content Type',
  };


  return (
    labels[reason] ||
    reason
  );
};